import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxSearchInput,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { useDebounce } from '@/hooks/useDebounce';
import {
  PCMP_GRANTABLE_MAX_CHARS,
  PCMP_GRANTABLE_MIN_CHARS,
  pcmpGrantableUsersQueryOptions,
} from '../../api/pcmpEditPermissions.api';

export interface PcmpGrantPickedUser {
  id: number;
  name: string;
}

interface PcmpGrantUserPickerProps {
  value: PcmpGrantPickedUser | null;
  onChange: (value: PcmpGrantPickedUser | null) => void;
  id?: string;
}

interface UserOption extends PcmpGrantPickedUser {
  detail: string;
}

/** Type-ahead over the active General Users an Admin can grant PCMP edit permissions to. */
export function PcmpGrantUserPicker({ value, onChange, id }: PcmpGrantUserPickerProps) {
  const [search, setSearch] = useState('');
  const typed = search.trim();
  const term = useDebounce(typed);
  const isTooShort = typed.length < PCMP_GRANTABLE_MIN_CHARS;
  // The results belong to what is typed only once the debounce has caught up.
  const isCurrent = term === typed;

  const { data, isFetching, isPlaceholderData, isError, refetch } = useQuery(
    pcmpGrantableUsersQueryOptions(term),
  );
  const isSearching = !isTooShort && (!isCurrent || isFetching);

  const options = useMemo<UserOption[]>(
    () =>
      isTooShort || !isCurrent || isPlaceholderData
        ? []
        : (data?.map((user) => ({
            id: user.userId,
            name: user.hrEmpId ? `${user.fullName} (${user.hrEmpId})` : user.fullName,
            detail: [user.designationName, user.departmentName].filter(Boolean).join(' · '),
          })) ?? []),
    [data, isTooShort, isCurrent, isPlaceholderData],
  );

  return (
    <Combobox<PcmpGrantPickedUser | null>
      items={options}
      value={value}
      onValueChange={(next) => onChange(next ? { id: next.id, name: next.name } : null)}
      inputValue={search}
      onInputValueChange={setSearch}
      filter={null}
      itemToStringLabel={(item) => item?.name ?? ''}
      isItemEqualToValue={(item, selected) => item?.id === selected?.id}
    >
      <ComboboxTrigger id={id} className="h-10">
        <ComboboxValue placeholder="Select a General User" />
        <ComboboxIcon />
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxSearchInput
          placeholder="Search by name, username or HR id..."
          maxLength={PCMP_GRANTABLE_MAX_CHARS}
        />
        <ComboboxList>
          {options.map((option) => (
            <ComboboxItem key={option.id} value={option}>
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{option.name}</span>
                {option.detail ? (
                  <span className="text-xs text-muted-foreground">{option.detail}</span>
                ) : null}
              </div>
            </ComboboxItem>
          ))}
        </ComboboxList>
        {isSearching ? (
          <div className="flex justify-center py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        {isTooShort ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            Type at least {PCMP_GRANTABLE_MIN_CHARS} characters to search.
          </p>
        ) : isError && !isSearching ? (
          <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-destructive">
            <span>Could not search users.</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : isSearching ? null : (
          <ComboboxEmpty>No General Users found.</ComboboxEmpty>
        )}
      </ComboboxContent>
    </Combobox>
  );
}
